import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { TbCalculator } from 'react-icons/tb';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { trackEvent } from '../utils/analytics';

const { FiX, FiExternalLink } = FiIcons;

const Calculator = () => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);
  const [tickets, setTickets] = useState(500);
  const [price, setPrice] = useState(50);

  // Debounced tracking of savings calculations
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      trackEvent('calculate_savings', {
        tickets_count: tickets,
        ticket_price: price,
        projected_savings: savings,
        savings_percent: savingsPercent
      });
    }, 1000); // 1s debounce to avoid flooding events during slider dragging

    return () => clearTimeout(delayDebounce);
  }, [tickets, price]);

  // Example logic: Competitor charges 5% + $1.50 per ticket. We charge 2.99% + $0.49.
  const compFeePerTicket = (price * 0.05) + 1.50;
  const ourFeePerTicket = (price * 0.0299) + 0.49;
  
  const compTotalFee = compFeePerTicket * tickets;
  const ourTotalFee = ourFeePerTicket * tickets;
  
  const savings = Math.max(0, compTotalFee - ourTotalFee);
  const savingsPercent = compTotalFee > 0 ? Math.round((savings / compTotalFee) * 100) : 0;

  return (
    <section id="calculator" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold uppercase tracking-wider mb-4">
            {t('calculator.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
            {t('calculator.title')}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('calculator.desc')}
          </p>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl border border-slate-200 shadow-2xl shadow-slate-200/50 p-8 md:p-12"
        >
          <div className="grid md:grid-cols-2 gap-12 items-center">
            
            {/* Controls */}
            <div className="space-y-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6">{t('calculator.estimateTitle')}</h3>
              
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-semibold text-slate-700">{t('calculator.ticketsLabel')}</label>
                  <span className="text-sm font-bold text-indigo-600">{tickets}</span>
                </div>
                <input 
                  type="range" 
                  min="50" max="5000" step="50" 
                  value={tickets} 
                  onChange={(e) => setTickets(Number(e.target.value))}
                />
                <div className="flex justify-between mt-1 text-xs text-slate-400 font-medium">
                  <span>50</span>
                  <span>5,000</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-semibold text-slate-700">{t('calculator.priceLabel')}</label>
                  <span className="text-sm font-bold text-indigo-600">{price}</span>
                </div>
                <input 
                  type="range" 
                  min="10" max="500" step="10" 
                  value={price} 
                  onChange={(e) => setPrice(Number(e.target.value))}
                />
                <div className="flex justify-between mt-1 text-xs text-slate-400 font-medium">
                  <span>10</span>
                  <span>500</span>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-indigo-50 rounded-2xl p-8 text-center border border-indigo-100 relative overflow-hidden">
               {/* Decorative background element */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-200 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2"></div>
              
              <div className="relative z-10">
                <p className="text-sm font-semibold text-indigo-800 uppercase tracking-wider mb-2">
                  {t('calculator.savingsLabel')}
                </p>
                <div className="text-5xl md:text-6xl font-extrabold text-indigo-600 mb-2 tracking-tight">
                  {savings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold mb-4 border border-emerald-200 shadow-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  {t('calculator.savePercent', { percent: savingsPercent })}
                </div>
                <p className="text-sm text-slate-600">
                  {t('calculator.basedOn', { tickets, price })}
                </p>
              </div>
            </div>

          </div>

          {/* Advanced Fee Calculator Button */}
          <div className="flex justify-center border-t border-slate-100 pt-8 mt-8">
            {/* Rotating outline wrapper */}
            <div className="relative group">
              {/* Spinning conic-gradient ring */}
              <div
                className="absolute -inset-[1.5px] z-0 pointer-events-none"
                style={{
                  borderRadius: '18px',
                  background: 'conic-gradient(from var(--angle, 0deg), #6366f1, #8b5cf6, #ec4899, #f59e0b, #6366f1)',
                  animation: 'spin-border 2.5s linear infinite',
                }}
              />
              <style dangerouslySetInnerHTML={{ __html: `
                @property --angle {
                  syntax: '<angle>';
                  initial-value: 0deg;
                  inherits: false;
                }
                @keyframes spin-border {
                  to { --angle: 360deg; }
                }
              `}} />
              <button
                onClick={() => {
                  trackEvent('open_advanced_calculator');
                  setIsModalOpen(true);
                }}
                className="relative z-10 bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 hover:from-indigo-500 hover:via-violet-500 hover:to-purple-500 text-white px-8 py-4 rounded-2xl font-bold flex items-center gap-2 transition-all shadow-xl shadow-indigo-600/20 hover:shadow-indigo-600/30 active:scale-95"
              >
                <SafeIcon icon={TbCalculator} className="w-5 h-5" />
                Advanced Fee Calculator
              </button>
            </div>
          </div>

        </motion.div>
      </div>

      {/* Advanced Calculator Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />

            {/* Modal Body */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="relative bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-screen-2xl h-[92vh] flex flex-col overflow-hidden z-10"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                    <SafeIcon icon={TbCalculator} className="w-4 h-4" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg">Advanced Fee Calculator</h3>
                </div>
                <div className="flex items-center gap-3">
                  <a 
                    href="https://velvety-granita-09ffd4.netlify.app" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
                    title="Open in new tab"
                  >
                    <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
                  </a>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Webview / Iframe container */}
              <div className="flex-1 bg-slate-50 relative">
                <iframe
                  src="https://velvety-granita-09ffd4.netlify.app"
                  title="Advanced Fee Calculator"
                  className="w-full h-full border-0 absolute inset-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </section>
  );
};

export default Calculator;